import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { Shield, Lock, Users, Camera, Mail, ChevronDown, Clock } from "lucide-react";
import { toast } from "sonner";

const PERMISSION_META = {
  ESSENTIAL_ACCOUNT: {
    icon: Lock,
    label: "Essential Account",
    description:
      "Required for your account to function. This covers the processing necessary to maintain your membership, credential, and access to LV Pass.",
    required: true,
  },
  CONNECTED_ACTIVITY: {
    icon: Users,
    label: "Connected Activity",
    description:
      "Allow your preferred name and LV member code to be visible to other members in features like Find a Game. No personal contact information is shared.",
    required: false,
  },
  PHOTOS_MEDIA: {
    icon: Camera,
    label: "Photos & Media",
    description:
      "Allow your image to appear in community event photos and media shared within the LV Pass platform.",
    required: false,
  },
  MARKETING: {
    icon: Mail,
    label: "Marketing Communications",
    description:
      "Receive optional communications about community updates, partner offers, and new features beyond essential account notifications.",
    required: false,
  },
};

const PERMISSION_ORDER = [
  "ESSENTIAL_ACCOUNT",
  "CONNECTED_ACTIVITY",
  "PHOTOS_MEDIA",
  "MARKETING",
];

function PermissionToggle({ permType, granted, updatedAt, onToggle, isPending }) {
  const meta = PERMISSION_META[permType];
  if (!meta) return null;
  const Icon = meta.icon;

  return (
    <div
      className="bg-walnut border border-lv-border p-5 sm:p-6"
      data-testid={`permission-${permType.toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <Icon size={18} className="text-bronze shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm text-cream tracking-wide font-medium">
                {meta.label}
              </h3>
              {meta.required && (
                <span className="text-[9px] tracking-[0.2em] uppercase text-bronze/70 bg-bronze/10 px-2 py-0.5">
                  Required
                </span>
              )}
            </div>
            <p className="text-xs text-sand/60 leading-relaxed mt-1.5">
              {meta.description}
            </p>
            {updatedAt && (
              <p className="text-[10px] text-lv-muted/50 mt-2">
                Last updated {new Date(updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            )}
          </div>
        </div>
        {meta.required ? (
          <div
            className="w-11 h-6 bg-lv-active/30 rounded-full flex items-center justify-end px-0.5 shrink-0 opacity-60 cursor-not-allowed"
            data-testid={`permission-toggle-${permType.toLowerCase()}`}
          >
            <div className="w-5 h-5 bg-lv-active rounded-full" />
          </div>
        ) : (
          <button
            onClick={() => onToggle(permType, !granted)}
            disabled={isPending}
            className={`w-11 h-6 rounded-full flex items-center px-0.5 shrink-0 transition-colors duration-200 ${
              granted
                ? "bg-lv-active/30 justify-end"
                : "bg-lv-border justify-start"
            }`}
            data-testid={`permission-toggle-${permType.toLowerCase()}`}
          >
            <div
              className={`w-5 h-5 rounded-full transition-colors duration-200 ${
                granted ? "bg-lv-active" : "bg-lv-muted/50"
              }`}
            />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Privacy() {
  const queryClient = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);

  const { data: permsData, isLoading } = useQuery({
    queryKey: ["member-permissions"],
    queryFn: () => api.get("/member/permissions"),
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ["member-permissions-history"],
    queryFn: () => api.get("/member/permissions/history"),
    enabled: showHistory,
  });

  const toggleMut = useMutation({
    mutationFn: ({ permType, granted }) =>
      api.post("/member/permissions", {
        permission_type: permType,
        granted,
        consent_text: granted
          ? `I consent to ${PERMISSION_META[permType]?.label}.`
          : null,
        language: "en",
      }),
    onSuccess: (_, { permType, granted }) => {
      toast.success(
        `${PERMISSION_META[permType]?.label} ${granted ? "enabled" : "disabled"}`
      );
      queryClient.invalidateQueries({ queryKey: ["member-permissions"] });
    },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="privacy-loading">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  const permissions = permsData?.permissions || {};

  return (
    <div className="max-w-2xl mx-auto" data-testid="privacy-page">
      <div className="mb-10 sm:mb-14">
        <h1 className="font-serif text-3xl sm:text-4xl text-cream tracking-wide">
          Privacy & Permissions
        </h1>
        <p className="text-sm text-lv-muted tracking-wide mt-2">
          Control how your information is used within LV Pass
        </p>
      </div>

      <div className="space-y-3" data-testid="permissions-list">
        {PERMISSION_ORDER.map((ptype) => (
          <PermissionToggle
            key={ptype}
            permType={ptype}
            granted={permissions[ptype]?.granted ?? (ptype === "ESSENTIAL_ACCOUNT")}
            updatedAt={permissions[ptype]?.updated_at}
            onToggle={(pt, granted) => toggleMut.mutate({ permType: pt, granted })}
            isPending={toggleMut.isPending}
          />
        ))}
      </div>

      {/* Audit history — secondary */}
      <div className="mt-10 pt-8 border-t border-lv-border/30">
        <button
          onClick={() => setShowHistory((v) => !v)}
          className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-lv-muted hover:text-bronze transition-colors w-full"
          data-testid="privacy-history-toggle"
        >
          <Clock size={14} />
          Permission History
          <ChevronDown
            size={14}
            className={`ml-auto transition-transform ${showHistory ? "rotate-180" : ""}`}
          />
        </button>
        {showHistory && (
          <div className="mt-4" data-testid="privacy-history">
            {historyLoading ? (
              <div className="w-8 h-0.5 bg-bronze mx-auto animate-pulse my-6" />
            ) : historyData?.length === 0 ? (
              <p className="text-xs text-lv-muted/50 py-4">No history recorded yet.</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {(historyData || []).map((r, idx) => (
                  <div
                    key={idx}
                    className="flex items-start justify-between gap-3 py-2.5 border-b border-lv-border/20 last:border-0"
                  >
                    <div className="min-w-0">
                      <p className="text-xs text-cream/80">
                        {r.permission_type
                          ? PERMISSION_META[r.permission_type]?.label || r.permission_type
                          : r.consent_type}
                      </p>
                      <p className="text-[10px] text-lv-muted/50 mt-0.5">
                        {r.accepted ? "Granted" : "Withdrawn"}
                        {r.source ? ` \u00b7 ${r.source}` : ""}
                      </p>
                    </div>
                    <span className="text-[10px] text-lv-muted/40 shrink-0">
                      {r.created_at
                        ? new Date(r.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
